import { each, reduce } from "lodash";
import { ParentTask } from "~/normalizr/entities";

export type HierarchyEntity<T> = T & {
    idAndName?: string;
    hierarchyPart?: string;
};

export type TaskHierarchyActivity = HierarchyEntity<ParentTask>;

const htmlAppearance = {
    verticalDash: '<div class="vert-dash"></div>',
    verticalRightDash: '<div class="vert-right-dash"></div>',
    noDash: '<div class="no-dash"></div>',
    cornerRightDash: '<div class="corner-right-dash"></div>',
};

type AppearanceCustomization = typeof htmlAppearance;

type TransformToHierarchyViewOptions<T, TValue = number> = {
    entities: T[];
    getId: F1<T, TValue>;
    getLevel: F1<T, number>;
    getName: F1<T, string>;
    combineIdAndName: boolean;
    graphicalAppearance?: AppearanceCustomization;
};

export const transformToHierarchyView = <T, TValue = number>({
    entities,
    graphicalAppearance = htmlAppearance,
    getId,
    getLevel,
    getName,
    combineIdAndName,
}: TransformToHierarchyViewOptions<T, TValue>): HierarchyEntity<T>[] => {
    if (!entities?.length) {
        return [];
    }

    entities = entities.reverse();

    // areLevelsBellowNotEmptyArr array on each iteration contains information if there are items with levels same or
    // less than current under current activity  if items are children of the ancestors of current activity
    // example:
    //   'item1',     areLevelsBellowNotEmptyArr = []    current level = 0
    // (items with level 0 always have empty hierarchy part)

    //   '├ item2',   areLevelsBellowNotEmptyArr = [true]              current level = 1
    // (level[1] == true, cause item6 is under item2 and parent of item6(item1) is ancestor of item2

    //   '|L item3',  areLevelsBellowNotEmptyArr = [true, false]       current level = 2
    // (level[1] == true, cause item6 is under item3 and parent of item6(item1) is ancestor of item3
    // (level[2] == false, cause item7 is under item3 but parent of item7(item6) is not ancestor of item3

    //   '|_├ item4',  areLevelsBellowNotEmptyArr = [true, false, true] current level = 3
    // (level[1] == true, cause item6 is under item4 and parent of item6(item1) is ancestor of item4
    // (level[2] == false, cause item7 is under item4 but parent of item7(item6) is not ancestor of item4
    // (level[3] == true, cause item5 is under item4 and parent of item5(item3) is ancestor of item4

    //   '|_L item5',  areLevelsBellowNotEmptyArr = [true, false, false] current level = 3
    // (level[1] == true, cause item6 is under item5 and parent of item6(item1) is ancestor of item5
    // (level[2] == false, cause item7 is under item5 but parent of item7(item6) is not ancestor of item5

    //   'L item6',    areLevelsBellowNotEmptyArr = [false],             current level = 1
    // (no items with levels same or less than 1 under current activity)

    //   '_L item7'    areLevelsBellowNotEmptyArr = [false, false]       current level = 2
    // (no items with levels same or less than 2 under current activity)

    let areLevelsBellowNotEmptyArr: boolean[] = [];

    const result = each(entities, (item: HierarchyEntity<T>) => {
        const level = getLevel(item);
        const name = getName(item);
        const id = getId(item);

        let transformedText = "";

        const taskLevel = level - 1;
        if (taskLevel < 0) {
            areLevelsBellowNotEmptyArr = [];
        } else {
            // we reduce areLevelsBellowNotEmptyArr size to level of current activity< cuase activityes
            // with greater level don't inflience on hierarchypart
            areLevelsBellowNotEmptyArr.length = taskLevel + 1;

            transformedText = reduce(
                areLevelsBellowNotEmptyArr,
                (memo, levelBellowNotEmpty, index) => {
                    if (index === taskLevel) {
                        // if there are items bellow of the same level as the curent activity, we should add ├, otherwise 'L'
                        return (
                            memo +
                            (levelBellowNotEmpty
                                ? graphicalAppearance.verticalRightDash
                                : graphicalAppearance.cornerRightDash)
                        );
                    }

                    // if there are items bellow of the less level than the curent activity, we should add |, otherwise '_'
                    return memo + (levelBellowNotEmpty ? graphicalAppearance.verticalDash : graphicalAppearance.noDash);
                },
                ""
            );

            areLevelsBellowNotEmptyArr[taskLevel] = true;
        }

        item.hierarchyPart = transformedText;

        if (combineIdAndName) {
            item.idAndName = id >= 0 ? id + " · " + name : name;
        }
    });

    return result.reverse();
};

const getTaskId = (item: ParentTask) => item.TaskID;
const getTaskLevel = (item: ParentTask) => item.TaskHierarchyLevel;
const getTaskName = (item: ParentTask) => item.Name;

type TransformTasksToHierarchyView<T> = Pick<
    TransformToHierarchyViewOptions<T>,
    "entities" | "graphicalAppearance" | "combineIdAndName"
>;
export const transformTasksToHierarchyView = <T extends ParentTask>({
    entities,
    combineIdAndName,
    graphicalAppearance,
}: TransformTasksToHierarchyView<T>) =>
    transformToHierarchyView<T>({
        entities,
        getId: getTaskId,
        getLevel: getTaskLevel,
        getName: getTaskName,
        combineIdAndName,
        graphicalAppearance,
    });
